// Forsiden: titel, de tre veje ind (alene, med en ven, med Volt), sværhed og baner. Kommer man fra en bane i gang, står Fortsæt øverst.

import { LEVELS, levelUnlocked } from '../../content/levels';
import { DIFFICULTIES, DIFFICULTY_ORDER } from '../../sim';
import type { DifficultyId } from '../../sim';
import { startBudget } from '../../sim/economy';
import { bestStars, levelWon, starsByDifficulty } from '../../storage';
import { h } from '../dom';
import { ICON } from '../icons';
import { campaignPanel } from './campaign';
import { modeButton, playModes } from './modes';
import { starsRow } from './screens';

export const BLURB: Record<DifficultyId, string> = {
  laerling: 'God tid og masser af varsel',
  vagthavende: 'Som en rigtig vagt',
  systemansvarlig: 'N-1: hver by skal have to veje ind',
  orkan: 'Alt på én gang. Mesterkrav: to veje ind til alle',
};

interface Rule {
  key: string;
  icon: string;
  text: string;
  label: string;
}

/** Sværhedens regler som ikon, kort tekst og en forklaring til skærmlæsere. Rækkefølgen er fast. */
export function rulesOf(id: DifficultyId): Rule[] {
  const d = DIFFICULTIES[id];
  const COACH = { full: 'Hånd viser vej', urgent: 'Hjælp ved fare', ring: 'Kun ring', off: 'Ingen hjælp' };
  // Orkan: ringen hjælper i hver banes første år, derefter intet (spor "sværhed").
  // Lærling: hånden slipper fra bane 3, så man har læst kortet selv før Vagthavende (IGN-løkke 2).
  const fade = d.coachFade ? d.coachFade.fromLevel - 1 : 0;
  const coach = d.firstYearCoach && d.firstYearCoach !== d.coach ? 'Ring år 1' : fade ? `Hånd bane 1-${fade}` : COACH[d.coach];
  const coachLabel =
    d.firstYearCoach && d.firstYearCoach !== d.coach
      ? 'En ring viser det vigtigste i første år, derefter ingen hjælp'
      : fade
        ? `Hånd på bane 1-${fade}; siden i første år og ved fare`
        : coach;
  const n1 = {
    shield: ['To veje: skjold', 'En by med to veje ind får et skjold'],
    bonus: ['N-1 til ★★★', 'Store byer skal have to veje ind for tre stjerner'],
    required: ['N-1 overalt', 'Alle byer skal have to veje ind for tre stjerner'],
    cascades: ['Kaskader', 'En overbelastet ledning slår fra og trækker naboerne med'],
  }[d.n1];
  const kr = startBudget(d.startBudgetFactor);
  const ev = d.maxConcurrent;
  return [
    { key: 'trust', icon: ICON.heart, text: String(d.trust), label: `${d.trust} hjerter` },
    { key: 'clock', icon: ICON.clock, text: `${d.clockS} s`, label: `${d.clockS} sekunder før en by går sort` },
    { key: 'coach', icon: ICON.hand, text: coach, label: coachLabel },
    { key: 'n1', icon: ICON.reinforce, text: n1[0] as string, label: n1[1] as string },
    { key: 'events', icon: ICON.bolt, text: `${ev} ad gangen`, label: `Højst ${ev} ${ev === 1 ? 'hændelse' : 'hændelser'} på én gang` },
    { key: 'cash', icon: ICON.coin, text: `${kr} kr`, label: `${kr} kr i kassen fra start` },
    { key: 'retry', icon: ICON.retry, text: id === 'orkan' ? '1 omspil' : 'Omspil', label: id === 'orkan' ? 'Ét omspil af et år' : 'Prøv året igen så tit du vil' },
    {
      key: 'pause',
      icon: ICON.pause,
      text: d.buildInPause ? 'Byg i pause' : 'Planlæg i pause',
      label: d.buildInPause ? 'Du kan bygge, mens spillet holder pause' : 'Træk op til tre ledninger i pause; de bygges, når tiden går',
    },
  ];
}

/**
 * Reglerne for sværheden som små ikoner med et ord: hjerter, mørkeur, hjælp, N-1, hændelser, kasse,
 * omspil og byg i pause. En regel, der er skærpet siden niveauet før, står med en prik og fed tekst,
 * så man kan se, hvad hvert trin ændrer (spor "sværhed"; IGN-løkke runde 2: "forskellen mellem
 * niveauerne står kun i en undertekst").
 */
export function diffRules(id: DifficultyId): HTMLElement {
  const i = DIFFICULTY_ORDER.indexOf(id);
  const prev = i > 0 ? rulesOf(DIFFICULTY_ORDER[i - 1] as DifficultyId) : null;
  const rule = (r: Rule): HTMLElement => {
    const changed = !!prev && prev.find((p) => p.key === r.key)?.text !== r.text;
    const label = changed ? `${r.label}. Skærpet` : r.label;
    return h(
      'span',
      { class: changed ? 'rule changed' : 'rule', 'aria-label': label, title: label },
      h('span', { class: 'rule-icon', html: r.icon }),
      h('span', { 'aria-hidden': 'true' }, r.text),
    );
  };
  return h('span', { class: 'diff-rules' }, ...rulesOf(id).map(rule));
}

export interface HomeHandlers {
  play(level: string): void;
  /** Ny sværhed; `level` er den bane, der er valgt lige nu, så skiftet ikke nulstiller den. */
  difficulty(d: DifficultyId, level: string): void;
  /** Sat, når en bane er i gang: Fortsæt tager spilleren tilbage, præcis hvor de var. */
  resume?: { title: string; go(): void };
  /** Tilbage til titlen. */
  back?(): void;
  /** Spil banen sammen med Volt, AI-makkeren (M3). */
  volt?(level: string): void;
  /** Makkerspil (M4): to telefoner, samme net. */
  friend?(): void;
  /** Dagens vagt: samme bane, frø og modifikator for alle i dag. */
  daily?(): void;
}

export function homeScreen(diff: DifficultyId, selectedLevel: string, handlers: HomeHandlers): HTMLElement {
  // En låst bane kan ikke vælges: står den valgt (fx fra en gammel gemning), vælges den seneste åbne.
  const open = (id: string): boolean => levelUnlocked(id, levelWon);
  let level = open(selectedLevel) ? selectedLevel : ([...LEVELS].reverse().find((l) => open(l.id))?.id ?? LEVELS[0]?.id ?? selectedLevel);
  const levelBtns: HTMLButtonElement[] = [];
  const levels = h(
    'div',
    { class: 'levels', role: 'radiogroup', 'aria-label': 'Bane' },
    ...LEVELS.map((lv, i) => {
      const n = bestStars(lv.id, diff);
      const unlocked = open(lv.id);
      const prev = LEVELS[i - 1];
      const other = otherStars(lv.id, diff);
      const sub = unlocked ? String(lv.year) : `Vind bane ${prev?.number ?? i} for at åbne`;
      const b = h(
        'button',
        {
          type: 'button',
          role: 'radio',
          class: unlocked ? 'level-card' : 'level-card locked',
          'data-level': lv.id,
          'aria-checked': String(lv.id === level),
          ...(unlocked ? {} : { 'aria-disabled': 'true', 'aria-label': `Bane ${lv.number}, ${lv.title}. Låst: ${sub}` }),
          onclick: () => {
            if (!unlocked) {
              b.classList.remove('nope');
              void b.offsetWidth;
              b.classList.add('nope');
              return;
            }
            level = lv.id;
            for (const x of levelBtns) x.setAttribute('aria-checked', String(x === b));
            for (const m of root.querySelectorAll('.dk-mark')) m.classList.toggle('sel', m.getAttribute('data-level') === lv.id);
          },
        },
        h('span', { class: 'level-num', ...(unlocked ? {} : { html: ICON.lock }) }, unlocked ? String(lv.number) : ''),
        h(
          'span',
          { class: 'level-text' },
          h('b', {}, lv.title),
          h('span', {}, sub),
          unlocked && other ? h('span', { class: 'level-other' }, other) : null,
        ),
        unlocked ? starsRow(n, { on: ICON.star, off: ICON.starOff }) : null,
      );
      levelBtns.push(b);
      return b;
    }),
  );
  const diffs = h(
    'div',
    { class: 'diffs', role: 'radiogroup', 'aria-label': 'Sværhed' },
    ...DIFFICULTY_ORDER.map((id) =>
      h(
        'button',
        {
          type: 'button',
          role: 'radio',
          class: 'diff',
          'aria-checked': String(id === diff),
          onclick: () => handlers.difficulty(id, level),
        },
        h('b', {}, DIFFICULTIES[id].name),
        h('span', {}, BLURB[id]),
        diffRules(id),
      ),
    ),
  );
  const root = h(
    'div',
    { class: 'card home' },
    h(
      'div',
      { class: 'title-block' },
      h('p', { class: 'eyebrow' }, 'Fuld spænding'),
      h('h1', { class: 'title small' }, 'VÆLG BANE'),
    ),
    ...(handlers.resume
      ? [
          h(
            'button',
            { type: 'button', class: 'btn primary big', autofocus: true, onclick: () => handlers.resume?.go() },
            h('span', { html: ICON.play }),
            `Fortsæt ${handlers.resume.title}`,
          ),
        ]
      : []),
    // De tre veje ind: alene, med en ven og med Volt. Banen og sværheden nedenfor gælder for alle tre
    // (med en ven vælger værten i rummet).
    playModes(
      [
        modeButton('solo', () => handlers.play(level), { hint: true, autofocus: !handlers.resume }),
        handlers.friend ? modeButton('friend', () => handlers.friend?.(), { hint: true }) : null,
        handlers.volt ? modeButton('volt', () => handlers.volt?.(level), { hint: true }) : null,
      ],
      'trio',
    ),
    // Kampagnen: Danmarkskortet med banerne, titlen og Dagens vagt (spor "indhold").
    campaignPanel(diff, level, () => handlers.daily?.()),
    h(
      'h2',
      { class: 'section sec-levels' },
      'Bane',
      h('small', { class: 'levels-note' }, `Stjerner tæller pr. sværhed · ${DIFFICULTIES[diff].name}`),
    ),
    levels,
    h('h2', { class: 'section sec-diffs' }, 'Sværhed'),
    diffs,
    handlers.back ? h('div', { class: 'actions home-back' }, h('button', { type: 'button', class: 'btn ghost', onclick: () => handlers.back?.() }, 'Tilbage')) : null,
  );
  return root;
}

/** Bedste stjerner på en anden sværhed end den valgte, fx "Lærling ★★". Tom, hvis der ingen er. */
export function otherStars(level: string, diff: DifficultyId): string {
  const by = starsByDifficulty(level);
  let best: DifficultyId | null = null;
  for (const d of DIFFICULTY_ORDER) {
    if (d === diff) continue;
    const n = by[d] ?? 0;
    if (n > 0 && (best === null || n >= (by[best] ?? 0))) best = d;
  }
  if (best === null) return '';
  const n = by[best] ?? 0;
  return `${DIFFICULTIES[best].name} ${'★'.repeat(n)}`;
}
